import { Location } from '@angular/common'
import { Component, computed, inject, signal } from '@angular/core'
import { RouterLink } from '@angular/router'
import { PublicConfiguration } from '@ftr/contracts/api/configuration'
import { LocalTimeRange, Uuid } from '@ftr/contracts/type/shared'
import {
  AnchorComponent,
  ApiResult,
  ButtonComponent,
  EmptyStateComponent,
  IconButtonComponent,
  PageComponent,
  PageStyle,
  selectWith,
} from '@ftr/foundation'
import { AppPaths, RequestPaths } from '@ftr/routing-paths'
import { Store } from '@ngxs/store'
import { BehaviorSubject } from 'rxjs'
import { CartState, RemoveCartItems, UpdateCartItem } from '../../store'
import { RequestTypeUrlValue } from '../../types'
import { RequestTimesDisplayComponent } from '../shared'
import { RequestCartGroupMetadataComponent } from './cart-group-metadata'
import { CartItemFormComponent } from './cart-item-form'

/*
 * Cart is currently limited to recording orders.
 * A recording request type URL is required for correct user navigation.
 */
export const CART_ITEM_REQUEST_TYPE = RequestTypeUrlValue.Recordings

interface CartViewModel {
  configuration: PublicConfiguration
}

@Component({
  selector: 'ftr-request-cart',
  templateUrl: './request-cart.component.html',
  styleUrl: './request-cart.component.css',
  imports: [
    AnchorComponent,
    ButtonComponent,
    CartItemFormComponent,
    EmptyStateComponent,
    IconButtonComponent,
    PageComponent,
    RequestCartGroupMetadataComponent,
    RequestTimesDisplayComponent,
    RouterLink,
  ],
})
export class RequestCartComponent {
  private readonly store = inject(Store)
  protected readonly location = inject(Location)

  protected viewModel$!: ApiResult<CartViewModel | undefined>
  protected readonly refresh = new BehaviorSubject<void>(undefined)
  protected readonly pageStyle = PageStyle.Fluid

  protected readonly itemIdBeingEdited = signal<Uuid | null>(null)

  protected readonly cartGroups = selectWith(() => CartState.groupedCartItems)
  protected readonly courtSystem = selectWith(() => CartState.cartCourtSystem)
  protected readonly cartIsPopulated = computed<boolean>(() => this.cartGroups().length > 0)
  protected readonly checkoutPath = computed<string[] | undefined>(() => {
    const courtSystem = this.courtSystem()
    if (!courtSystem) {
      return undefined
    }

    return [
      '/',
      AppPaths.Request,
      CART_ITEM_REQUEST_TYPE,
      RequestPaths.CourtSystem,
      courtSystem.id,
      RequestPaths.Checkout,
    ]
  })
  protected readonly recordingsRequestPath = computed(() => [
    '/',
    AppPaths.Request,
    RequestPaths.Recordings,
    RequestPaths.ChooseCourt,
  ])
  protected readonly transcriptRequestPath = computed(() => [
    '/',
    AppPaths.Request,
    RequestPaths.Transcripts,
    RequestPaths.ChooseCourt,
  ])

  protected openEditItemForm(itemId: Uuid): void {
    this.itemIdBeingEdited.set(itemId)
  }

  protected closeEditItemForm(): void {
    this.itemIdBeingEdited.set(null)
  }

  protected removeItems(itemIds: string[]): void {
    this.store.dispatch(new RemoveCartItems(itemIds))
  }

  protected onItemTimeframesSaved(itemId: Uuid, requestedTimes: LocalTimeRange[]): void {
    this.store.dispatch(new UpdateCartItem(itemId, { requestedTimes }))
    this.closeEditItemForm()
  }
}
